import { useState, useEffect, useCallback, useRef } from 'react';
import { useSession } from '@/hooks/use-session';
import { useCalendarEvents } from '@/hooks/use-calendar-events';
import { useCalendarDate } from '@/hooks/use-calendar-date';
import { useCountrySelection } from '@/hooks/use-country-selection';
import { DayEvents } from '@/types/event';
import { toast } from '@/hooks/use-toast';

export const useCalendarState = () => {
  const { session, isLoading: isSessionLoading } = useSession();
  const { currentDate, handlePrevMonth, handleNextMonth } = useCalendarDate();
  const { selectedCountries, handleCountrySelect, handleClearFilters } = useCountrySelection();
  const [selectedDate, setSelectedDate] = useState<number>();
  const isMounted = useRef(true);
  const retryCount = useRef(0);
  const MAX_RETRIES = 3;

  const { data, isLoading: isDataLoading, error, isRefetching, refetch } = useCalendarEvents(
    currentDate,
    selectedCountries.length > 0 ? selectedCountries : undefined,
    session,
    {
      enabled: !!session?.user?.id && !isSessionLoading,
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes
      retry: 3,
    }
  );

  // Handle component cleanup
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Handle error states and auto-retry
  useEffect(() => {
    if (session?.user?.id && error) {
      if (retryCount.current < MAX_RETRIES && isMounted.current) {
        // Exponential backoff for retries
        const timeoutId = setTimeout(() => {
          if (isMounted.current) {
            retryCount.current += 1;
            refetch();
          }
        }, Math.min(1000 * (2 ** retryCount.current), 10000));

        return () => clearTimeout(timeoutId);
      } else {
        toast({
          variant: "destructive",
          title: "Error loading calendar",
          description: "Failed to load calendar data. Please try refreshing the page.",
        });
      }
    }
  }, [session, error, refetch]);

  const handleDateClick = useCallback((date: number) => {
    if (!isMounted.current) return;
    setSelectedDate(date);
  }, []);

  return {
    session,
    isSessionLoading,
    currentDate,
    handlePrevMonth,
    handleNextMonth,
    selectedCountries,
    handleCountrySelect,
    handleClearFilters,
    selectedDate,
    handleDateClick,
    data,
    isDataLoading,
    error,
    isRefetching,
    isMounted
  };
};